import { useLaunchDarkly } from '@/platform/composables/useLaunchDarkly';
import getUrlParam from '@/utils/helpers/getUrlParam';
import { trackPendoEvent } from '@/utils/helpers/pendo';

export { useBaseMetrics } from '@/platform/composables/useBaseMetrics';

const EXPERIMENT_BYPASS_SEARCH_PARAM = 'experimentDebug';

export default function useExperiment() {
  const { client: ldClient, ldTrackCustomEvent } = useLaunchDarkly();

  // try to stringify
  const stringify = (value) => {
    try {
      return JSON.stringify(value);
    } catch {
      return value;
    }
  };

  /**
   * Push experiment participation and variation to Pendo, if use is part of and experiment
   *
   * @param {string} ldFlagKey Name of the LaunchDarkly key for the experiment
   * @param {*} [defaultValue] Default value for the variation to use if value could not be fetched. defaults to `undefined`
   */
  const trackExperimentInPendo = (ldFlagKey, defaultValue) => {
    ldClient.value.waitUntilReady().then(() => {
      const expVariationDetail = ldClient.value.variationDetail(
        ldFlagKey,
        defaultValue,
      );

      if (
        expVariationDetail.reason?.inExperiment ||
        getUrlParam(EXPERIMENT_BYPASS_SEARCH_PARAM) === 'true'
      ) {
        let experimentVariationName = '';

        switch (expVariationDetail.value) {
          case true:
            experimentVariationName = 'test';
            break;
          case false:
            experimentVariationName = 'control';
            break;
          default:
            experimentVariationName = stringify(expVariationDetail.value);
        }

        trackPendoEvent('EXPERIMENT', {
          experiment_id: ldFlagKey,
          experiment_variation_index: expVariationDetail.variationIndex,
          experiment_variation_value: stringify(expVariationDetail.value),
          experiment_variation_name: experimentVariationName,
        });
      }
    });
  };

  /**
   * Submit a Pendo tracking event if user is in an active Launch Darkly experiment
   * @param {string} ldFlagKey Name of the LaunchDarkly key for the experiment
   * @param {string} eventName pendo event key
   * @param {Object} metadata pendo metadata object
   */
  const trackPendoEventIfInExperiment = (ldFlagKey, eventName, metadata) => {
    ldClient.value.waitUntilReady().then(() => {
      const expVariationDetail = ldClient.value.variationDetail(ldFlagKey);

      if (
        expVariationDetail.reason?.inExperiment ||
        getUrlParam(EXPERIMENT_BYPASS_SEARCH_PARAM) === 'true'
      ) {
        trackPendoEvent(eventName, metadata);
      }
    });
  };

  /**
   * Submit a LaunchDarkly custom event if user is in an active Launch Darkly experiment
   * @param {string} ldFlagKey Name of the LaunchDarkly key for the flag
   * @param {string} eventName LaunchDarkly custom event key
   * @param {*} [data] LaunchDarkly data, and JSON serializable value
   * @param {*} [metricKeyValue] LaunchDarkly metric value
   */
  const trackLDEventIfInExperiment = (
    ldFlagKey,
    eventName,
    data,
    metricValue,
  ) => {
    ldClient.value.waitUntilReady().then(() => {
      const expVariationDetail = ldClient.value.variationDetail(ldFlagKey);

      if (
        expVariationDetail.reason?.inExperiment ||
        getUrlParam(EXPERIMENT_BYPASS_SEARCH_PARAM) === 'true'
      ) {
        ldTrackCustomEvent(eventName, data, metricValue);
      }
    });
  };

  return {
    trackExperimentInPendo,
    trackPendoEvent,
    trackPendoEventIfInExperiment,
    trackLDEventIfInExperiment,
    ldTrackCustomEvent,
    ldVariationDetail: ldClient.value.variationDetail,
  };
}
